import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Repeat } from 'lucide-react';
import {
  allDayIso,
  calendarDate,
  dueState,
  moveToDate,
  shiftStartWithDue,
  startAfterDue,
  type BoardSnapshot,
  type CardSummary,
} from '@kaba/shared';
import { HolidayToggle, useHolidayDisplay } from '../HolidaySelect';
import { saveCardField } from '../../lib/cardFields';
import { addDays, cardDay, cardTime, daysBetween, resolveSource, sourceOptions, storedSource, storeSource, type CalendarSource } from '../../lib/calendarSource';
import { useUpdateCard } from '../../lib/board';
import { Button, cx, IconButton, LABEL_BG } from '../ui/ui';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const monthFmt = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' });
const dayFmt = new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
const timeFmt = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });

/** Lokales Datum als Schlüssel JJJJ-MM-TT */
export function dayKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfGrid(month: Date) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7; // Montag zuerst
  return new Date(first.getFullYear(), first.getMonth(), 1 - offset);
}

export function CalendarView({
  snapshot,
  visible,
  canEdit,
  onOpenCard,
}: {
  snapshot: BoardSnapshot;
  visible: (c: CardSummary) => boolean;
  canEdit: boolean;
  onOpenCard: (id: string) => void;
}) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [mode, setMode] = useState<'month' | 'agenda'>(() => (window.matchMedia('(max-width: 639px)').matches ? 'agenda' : 'month'));
  const [activeId, setActiveId] = useState<string | null>(null);
  const update = useUpdateCard(snapshot.board.id);

  const qc = useQueryClient();
  // 0.68: Datum des Kalenders (Frist, Start oder eigenes Datumsfeld) – persönlich je Board
  const [sourceValue, setSourceValue] = useState<string>(() => storedSource(snapshot.board.id));
  const options = sourceOptions(snapshot.fields);
  const src = resolveSource(sourceValue, snapshot.fields);
  const source = src.value;

  // 0.73: Feiertage ein- und ausblendbar (am Konto gemerkt), Bundesland wie bei Serien/Regeln

  const cards = useMemo(() => snapshot.cards.filter(visible), [snapshot.cards, visible]);
  const byDay = useMemo(() => {
    const m = new Map<string, CardSummary[]>();
    for (const c of cards) {
      const k = cardDay(c, source);
      if (!k) continue;
      m.set(k, [...(m.get(k) ?? []), c]);
    }
    for (const list of m.values()) list.sort((a, b) => ((cardTime(a, source) ?? '') < (cardTime(b, source) ?? '') ? -1 : 1));
    return m;
  }, [cards, source]);
  const undated = cards.filter((c) => !cardDay(c, source) && !c.completedAt);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 260, tolerance: 8 } }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over) return;
    const card = snapshot.cards.find((c) => c.id === String(active.id).slice(4));
    const target = String(over.id).slice(4); // "day:JJJJ-MM-TT"
    if (!card || !target) return;
    if (cardDay(card, source) === target) return;
    if (source !== 'due') return moveOther(card, target, source);
    const next = moveToDate(card.dueAt, card.dueHasTime, target);
    const hasTime = !!card.dueAt && card.dueHasTime;
    // Liegt der Start danach, rückt er um dieselbe Anzahl Tage mit
    const startAt = shiftStartWithDue(card, next, hasTime);
    update.mutate({ id: card.id, patch: { dueAt: next, dueHasTime: hasTime, ...startAt, expectedVersion: card.version } });
  };

  /** Start bzw. Datumsfeld auf einen Tag legen */
  const moveOther = (card: CardSummary, target: string, src: CalendarSource) => {
    if (src === 'start') {
      const old = card.startAt ? calendarDate(card.startAt, card.startHasTime) : null;
      const startAt = moveToDate(card.startAt, card.startHasTime, target);
      const startHasTime = !!card.startAt && card.startHasTime;
      // Läge der Start nach der Frist, rückt die Frist um dieselbe Anzahl Tage mit
      let due: { dueAt: string; dueHasTime: boolean } | undefined;
      if (card.dueAt && startAfterDue(startAt, startHasTime, card.dueAt, card.dueHasTime)) {
        const delta = old ? daysBetween(old, target) : daysBetween(calendarDate(card.dueAt, card.dueHasTime), target);
        let dueAt = moveToDate(card.dueAt, card.dueHasTime, addDays(calendarDate(card.dueAt, card.dueHasTime), delta));
        if (startAfterDue(startAt, startHasTime, dueAt, card.dueHasTime)) dueAt = card.dueHasTime ? dueAt : allDayIso(target);
        due = { dueAt, dueHasTime: card.dueHasTime };
      }
      update.mutate({ id: card.id, patch: { startAt, startHasTime, ...due, expectedVersion: card.version } });
      return;
    }
    const field = snapshot.fields.find((f) => f.id === src.slice(6));
    if (field) void saveCardField(qc, snapshot.board.id, card, field, target);
  };

  const today = dayKey(new Date());
  const gridStart = startOfGrid(month);
  const days = Array.from({ length: 42 }, (_, i) => new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
  const active = activeId ? snapshot.cards.find((c) => c.id === activeId) : null;

  const hol = useHolidayDisplay('calendarHolidays', [month.getFullYear() - 1, month.getFullYear(), month.getFullYear() + 1]);
  const holidays = hol.holidays;

  const shift = (n: number) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + n, 1));

  return (
    <DndContext
      sensors={canEdit ? sensors : undefined}
      onDragStart={({ active }) => {
        setActiveId(String(active.id).slice(4));
        if ('vibrate' in navigator) navigator.vibrate?.(8);
      }}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
      accessibility={{
        screenReaderInstructions: { draggable: 'Zum Ändern des Datums die Karte öffnen und es dort eintragen.' },
      }}
    >
      <div className="flex h-full flex-col">
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 sm:px-4">
          <IconButton label="Vorheriger Monat" onClick={() => shift(-1)}>
            <ChevronLeft size={20} />
          </IconButton>
          <h2 className="min-w-36 text-center text-base font-semibold capitalize">{monthFmt.format(month)}</h2>
          <IconButton label="Nächster Monat" onClick={() => shift(1)}>
            <ChevronRight size={20} />
          </IconButton>
          <Button size="sm" onClick={() => setMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>
            Heute
          </Button>
          <HolidayToggle display={hol} className="ml-auto" />
          <label className="flex items-center gap-1.5 text-sm">
            <span className="text-[var(--ink-soft)]">Datum</span>
            <select
              aria-label="Welches Datum zeigt der Kalender?"
              value={source}
              onChange={(e) => {
                setSourceValue(e.target.value);
                storeSource(snapshot.board.id, e.target.value as CalendarSource);
              }}
              className="min-h-10 max-w-44 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-base sm:min-h-9 sm:text-sm"
            >
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <div role="group" aria-label="Darstellung" className="flex rounded-lg bg-black/5 p-0.5 dark:bg-white/10">
            {(
              [
                ['month', 'Monat'],
                ['agenda', 'Agenda'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                aria-pressed={mode === k}
                onClick={() => setMode(k)}
                className={cx('min-h-9 rounded-md px-3 text-sm', mode === k ? 'bg-[var(--panel)] font-medium shadow-sm' : 'text-[var(--ink-soft)]')}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 gap-3 px-3 pb-24 sm:px-4 sm:pb-4">
          {mode === 'month' ? (
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--panel)]">
              <div className="grid grid-cols-7 border-b border-[var(--line)] text-center text-xs font-semibold text-[var(--ink-soft)]">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="py-1.5">
                    {w}
                  </div>
                ))}
              </div>
              <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6">
                {days.map((d) => {
                  const k = dayKey(d);
                  return (
                    <DayCell key={k} dayK={k} date={d} inMonth={d.getMonth() === month.getMonth()} isToday={k === today} holiday={holidays.get(k)}>
                      {(byDay.get(k) ?? []).map((c) => (
                        <Chip key={c.id} card={c} snapshot={snapshot} onOpen={onOpenCard} canEdit={canEdit} source={source} />
                      ))}
                    </DayCell>
                  );
                })}
              </div>
            </div>
          ) : (
            <Agenda month={month} byDay={byDay} holidays={holidays} snapshot={snapshot} onOpen={onOpenCard} canEdit={canEdit} today={today} source={source} noun={src.noun} />
          )}

          <aside className="hidden w-60 shrink-0 flex-col overflow-hidden rounded-2xl bg-[var(--column)] lg:flex">
            <h3 className="px-3 pt-3 pb-1 text-sm font-semibold">
              Ohne {src.noun} ({undated.length})
            </h3>
            <p className="px-3 pb-2 text-xs text-[var(--ink-soft)]">Auf einen Tag ziehen, um {source === 'due' ? 'die Frist' : source === 'start' ? 'den Start' : src.noun} zu setzen.</p>
            <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-2 pb-2">
              {undated.map((c) => (
                <Chip key={c.id} card={c} snapshot={snapshot} onOpen={onOpenCard} canEdit={canEdit} source={source} large />
              ))}
            </div>
          </aside>
        </div>
      </div>
      {createPortal(
        <DragOverlay dropAnimation={null}>
          {active ? (
            <div className="w-44 rotate-2 rounded-lg bg-[var(--panel)] px-2 py-1.5 text-sm shadow-2xl ring-2 ring-accent/50">{active.title}</div>
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

function DayCell({
  dayK,
  date,
  inMonth,
  isToday,
  holiday,
  children,
}: {
  dayK: string;
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  holiday?: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${dayK}` });
  return (
    <div
      ref={setNodeRef}
      aria-label={holiday ? `${dayFmt.format(date)}, Feiertag: ${holiday}` : dayFmt.format(date)}
      data-holiday={holiday}
      className={cx(
        'flex min-h-0 flex-col gap-0.5 overflow-hidden border-r border-b border-[var(--line)] p-1 [&:nth-child(7n)]:border-r-0',
        !inMonth && 'bg-black/[0.025] dark:bg-white/[0.02]',
        holiday && 'bg-amber-500/[0.08] dark:bg-amber-400/[0.08]',
        isOver && 'bg-accent-soft',
      )}
    >
      <div className="flex min-w-0 shrink-0 items-center gap-1">
        {holiday && (
          <>
            <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-amber-500 sm:hidden" />
            <span title={holiday} className="min-w-0 flex-1 truncate text-[11px] leading-tight text-amber-800 max-sm:hidden dark:text-amber-300">
              {holiday}
            </span>
          </>
        )}
        <span
          className={cx(
            'ml-auto inline-flex size-6 shrink-0 items-center justify-center rounded-full text-xs',
            isToday ? 'bg-accent font-semibold text-on-accent' : inMonth ? '' : 'text-[var(--ink-soft)]',
          )}
        >
          {date.getDate()}
        </span>
      </div>
      <div className="flex min-h-0 flex-col gap-0.5 overflow-y-auto">{children}</div>
    </div>
  );
}

function Chip({
  card,
  snapshot,
  onOpen,
  canEdit,
  large,
  source,
}: {
  card: CardSummary;
  snapshot: BoardSnapshot;
  onOpen: (id: string) => void;
  canEdit: boolean;
  large?: boolean;
  source: CalendarSource;
}) {
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({ id: `cal:${card.id}`, disabled: !canEdit });
  const state = card.dueAt ? dueState(card.dueAt, card.dueHasTime, card.completedAt) : 'none';
  const label = snapshot.labels.find((l) => card.labelIds.includes(l.id));
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onOpen(card.id)}
      data-card-id={card.id}
      onContextMenu={(e) => matchMedia('(pointer: coarse)').matches && e.preventDefault()}
      title={card.title}
      className={cx(
        'kaba-card flex w-full items-center gap-1 rounded-md px-1.5 text-left',
        large ? 'min-h-10 bg-[var(--panel)] text-sm shadow-sm' : 'min-h-6 text-[11px] leading-tight sm:text-xs',
        !large && (state === 'overdue' ? 'bg-rose-600 text-white' : state === 'done' ? 'bg-emerald-600/15 line-through opacity-70' : 'bg-accent-soft'),
        isDragging && 'opacity-30',
      )}
    >
      {label && <span className={cx('size-1.5 shrink-0 rounded-full', LABEL_BG[label.color])} />}
      {cardTime(card, source) && !large && <span className="shrink-0 opacity-80">{timeFmt.format(new Date(cardTime(card, source)!))}</span>}
      <span className="truncate">{card.title}</span>
      {card.recurrence && <Repeat size={10} className="shrink-0 opacity-70" />}
    </button>
  );
}

function Agenda({
  month,
  byDay,
  holidays,
  snapshot,
  onOpen,
  canEdit,
  today,
  source,
  noun,
}: {
  month: Date;
  byDay: Map<string, CardSummary[]>;
  holidays: Map<string, string>;
  snapshot: BoardSnapshot;
  onOpen: (id: string) => void;
  canEdit: boolean;
  today: string;
  source: CalendarSource;
  noun: string;
}) {
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const days = Array.from({ length: last }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1)).filter(
    (d) => byDay.has(dayKey(d)) || holidays.has(dayKey(d)) || dayKey(d) === today,
  );
  const cols = new Map(snapshot.columns.map((c) => [c.id, c.title]));
  return (
    <div className="mx-auto flex w-full max-w-2xl min-w-0 flex-1 flex-col gap-4 overflow-y-auto">
      {days.every((d) => !byDay.has(dayKey(d)) && dayKey(d) !== today) && <p className="p-4 text-sm text-[var(--ink-soft)]">In diesem Monat ist bei keiner Karte {source === 'due' ? 'eine Frist' : source === 'start' ? 'ein Start' : noun} eingetragen.</p>}
      {days.map((d) => {
        const k = dayKey(d);
        const list = byDay.get(k) ?? [];
        return (
          <AgendaDay key={k} dayK={k} label={dayFmt.format(d)} isToday={k === today} holiday={holidays.get(k)}>
            {list.length === 0 && !holidays.has(k) && <p className="px-3 py-2 text-sm text-[var(--ink-soft)]">{source === 'due' ? 'Nichts fällig.' : 'Nichts eingetragen.'}</p>}
            {list.map((c) => {
              const state = dueState(c.dueAt, c.dueHasTime, c.completedAt);
              return (
                <AgendaRow key={c.id} card={c} canEdit={canEdit} onOpen={onOpen}>
                  <span
                    className={cx(
                      'w-12 shrink-0 text-xs tabular-nums',
                      state === 'overdue' ? 'font-semibold text-rose-600' : 'text-[var(--ink-soft)]',
                    )}
                  >
                    {cardTime(c, source) ? timeFmt.format(new Date(cardTime(c, source)!)) : 'Ganztag'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cx('block truncate text-sm', c.completedAt && 'line-through opacity-60')}>{c.title}</span>
                    <span className="text-xs text-[var(--ink-soft)]">{cols.get(c.columnId)}</span>
                  </span>
                  {c.recurrence && <Repeat size={14} className="shrink-0 text-[var(--ink-soft)]" aria-label="Wiederkehrend" />}
                </AgendaRow>
              );
            })}
          </AgendaDay>
        );
      })}
    </div>
  );
}

function AgendaDay({ dayK, label, isToday, holiday, children }: { dayK: string; label: string; isToday: boolean; holiday?: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${dayK}` });
  return (
    <section
      ref={setNodeRef}
      className={cx('shrink-0 overflow-hidden rounded-2xl border bg-[var(--panel)]', isOver ? 'border-accent' : 'border-[var(--line)]')}
    >
      <h3 className={cx('px-3 py-2 text-sm font-semibold', isToday ? 'bg-accent text-on-accent' : 'bg-[var(--column)]')}>
        {isToday ? `Heute · ${label}` : label}
        {holiday && <span className={cx('ml-2 text-xs font-normal', isToday ? '' : 'text-amber-800 dark:text-amber-300')}>Feiertag: {holiday}</span>}
      </h3>
      <div className="flex flex-col divide-y divide-[var(--line)]">{children}</div>
    </section>
  );
}

function AgendaRow({ card, canEdit, onOpen, children }: { card: CardSummary; canEdit: boolean; onOpen: (id: string) => void; children: React.ReactNode }) {
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({ id: `cal:${card.id}`, disabled: !canEdit });
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onOpen(card.id)}
      data-card-id={card.id}
      onContextMenu={(e) => matchMedia('(pointer: coarse)').matches && e.preventDefault()}
      className={cx('kaba-card flex min-h-12 w-full items-center gap-3 px-3 text-left', isDragging && 'opacity-30')}
    >
      {children}
    </button>
  );
}
